/**
 * useViewSurface — ONE hook for the active view's projection, shared by every
 * surface that renders it: the board (LayoutProvider feeds `plan` into
 * `useBoardLayout`), the table and the matrix (they read the EFFECTIVE `view` +
 * `ctx` for `computeTableData`/`computeMatrixData`), and the HUD (diagnostics).
 *
 * It wires the full GAP-3 completion everywhere in one place: the tab-aware
 * effective view name, the session plane pick, live knob-picker values, the
 * builder draft, the workspace governance closure (policies + queries), view-
 * INSTANCE parent resolution, and the crash guard (a projection throw degrades
 * to the identity plan + a VIEW_PLAN_ERROR diagnostic — never an unmounted
 * tree). Surfaces consuming this hook cannot drift from the board.
 */
import { useMemo } from "react";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useActiveView } from "../workbench/ActiveViewContext.js";
import { useEffectiveViewName } from "./useEffectiveViewName.js";
import {
  collectViewGovernance,
  computeViewSurfaceResult,
  type ViewSurfaceResult,
} from "./viewPlan.js";

const EMPTY_KNOBS: Readonly<Record<string, number | string>> = {};

export function useViewSurface(): ViewSurfaceResult {
  const { resolvedModel, graph, compiledSources } = useWorkspaceModel();
  const activeViewName = useEffectiveViewName();
  const { activePlane, knobsByView, draftView, revealsByView } = useActiveView();
  // Click-reveal + knobs: the ACTIVE view's session slices (drafts key under
  // the reserved `::draft` slot; per-view keying prevents cross-view leaks).
  const revealViewKey = draftView ? "::draft" : activeViewName;
  const knobValues = (revealViewKey ? knobsByView[revealViewKey] : undefined) ?? EMPTY_KNOBS;
  const revealedKeys = useMemo(
    () => (revealViewKey ? new Set(revealsByView[revealViewKey] ?? []) : null),
    [revealsByView, revealViewKey],
  );
  // The workspace governance closure the projection's eval context needs so a
  // `style violating <P>` / query-ref selector resolves. Parse is content-
  // cached, so this re-derives only when the compile changes.
  const governance = useMemo(
    () => collectViewGovernance(resolvedModel, compiledSources),
    [resolvedModel, compiledSources],
  );
  return useMemo(
    () =>
      computeViewSurfaceResult(
        resolvedModel,
        graph,
        activeViewName,
        activePlane,
        knobValues,
        draftView,
        governance,
        revealedKeys,
      ),
    [resolvedModel, graph, activeViewName, activePlane, knobValues, draftView, governance, revealedKeys],
  );
}
